import { useState } from 'react'
import type { OpeningCard } from '../../../lib/contract'
import styles from './Dossier.module.css'
import { useT } from '../../../lib/i18n'

/**
 * THE DOSSIER — and THE DOSSIER RECEIVES (brief §5; four-doors §5.8 law 3).
 *
 * The paper on the desk, in two sets. GIVEN is what you were handed, in its
 * authored order; RECEIVED is what arrives during play — gated documents, the
 * results of research you asked for — appended by receipt.
 *
 * The seed is the opening run: every card the player tapped through is here
 * afterwards, at the head of the given set. That is what makes skimming safe —
 * nothing is consumed-and-gone, so the intro IS the reference material, and the
 * player learns on day one that paper kept is paper that pays.
 *
 * ⚠ [CONTRACT PENDING], and larger than the brief assumes. §5 reads as though a
 * drawer already exists over the pack's `documents/` set; neither is true today.
 * There is NO documents endpoint (`PackEndpoints` serves packs, assets-manifest,
 * onboarding, briefing — not documents), and no receiving-event shape. Adding
 * either is server work, and this mission is `web/src` only. So:
 *
 *   - the opening cards are REAL and render live;
 *   - given documents and received artifacts render only from what is passed in,
 *     which in production is nothing — absence, never a placeholder pretending
 *     to be a document (dumb-client law 3);
 *   - the dev harness under `routes/dev/` drives both against mocks, so the
 *     states are built and reviewable now and wire up when the contract lands.
 */

/** A received artifact, as the feed will eventually describe it. */
export interface ReceivedItem {
  id: string
  title: string
  /**
   * The provenance line, VERBATIM from the feed — "carried out by K., day 9".
   * The client never writes provenance: an invented one is a claim about how a
   * thing was come by, which is exactly the kind of thing the player is meant to
   * be able to trust.
   */
  provenance: string
  /** Received-at, as the feed stamped it. */
  at: string
  body: string
}

export interface GivenItem {
  id: string
  title: string
  body: string
  /**
   * Cards from the opening run read as paper; a title card reads as a cover.
   *
   * `plain` files here like any other — the card's REVEAL belongs to the run, not to the
   * dossier: a reader coming back to re-read a line should get it whole and immediately.
   * Filed content is reference material, whatever ceremony delivered it the first time.
   */
  kind?: OpeningCard['kind']
}

export function cardsAsGiven(cards: OpeningCard[]): GivenItem[] {
  return cards.map((c) => ({ id: c.id, title: c.title, body: c.body, kind: c.kind }))
}

export default function Dossier({
  given,
  received,
  onClose,
}: {
  given: GivenItem[]
  received: ReceivedItem[]
  onClose: () => void
}) {
  const t = useT()
  const [openId, setOpenId] = useState<string | null>(null)
  const [read, setRead] = useState<Set<string>>(new Set())

  const open = (id: string) => {
    setOpenId((cur) => (cur === id ? null : id))
    setRead((prev) => new Set(prev).add(id))
  }

  const empty = given.length === 0 && received.length === 0

  return (
    <aside className={styles.drawer} aria-label={t('desk.dossier')}>
      <header className={styles.head}>
        <h2 className={styles.title}>{t('desk.dossier')}</h2>
        <button className={styles.close} onClick={onClose} aria-label={t('desk.dossier.close')}>
          ×
        </button>
      </header>

      <div className={styles.body}>
        {empty && (
          /* Absence stated plainly. Not "loading", not a skeleton pretending
             paper is on its way — there is none, and that is a true report. */
          <p className={styles.none}>{t('desk.dossier.empty')}</p>
        )}

        {given.length > 0 && (
          <section className={styles.set}>
            <h3 className={styles.setHead}>{t('desk.dossier.given')}</h3>
            {given.map((item) => (
              <Sheet
                key={item.id}
                id={item.id}
                title={item.title}
                body={item.body}
                open={openId === item.id}
                onOpen={open}
              />
            ))}
          </section>
        )}

        {received.length > 0 && (
          <section className={styles.set}>
            <h3 className={styles.setHead}>{t('desk.dossier.received')}</h3>
            {received.map((item) => (
              <Sheet
                key={item.id}
                id={item.id}
                title={item.title}
                body={item.body}
                provenance={item.provenance}
                at={item.at}
                unread={!read.has(item.id)}
                open={openId === item.id}
                onOpen={open}
              />
            ))}
          </section>
        )}
      </div>
    </aside>
  )
}

function Sheet({
  id,
  title,
  body,
  provenance,
  at,
  unread,
  open,
  onOpen,
}: {
  id: string
  title: string
  body: string
  provenance?: string
  at?: string
  unread?: boolean
  open: boolean
  onOpen: (id: string) => void
}) {
  const t = useT()
  return (
    <article className={open ? styles.sheetOpen : styles.sheet}>
      <button className={styles.sheetHead} onClick={() => onOpen(id)} aria-expanded={open}>
        {unread && <span className={styles.unread} aria-label={t('desk.dossier.unread')} />}
        <span className={styles.sheetTitle}>{title}</span>
      </button>

      {open && (
        <div className={styles.sheetBody}>
          {/* Provenance and stamp ride ABOVE the text, the way a docket does —
              and both are the feed's words, reproduced, never composed here. */}
          {provenance && <p className={styles.provenance}>{provenance}</p>}
          {at && <p className={styles.stamp}>{at}</p>}
          {body.split(/\n{2,}/).map((para, i) => (
            <p key={i} className={styles.para}>
              {para}
            </p>
          ))}
        </div>
      )}
    </article>
  )
}
